import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  makeStyles, tokens, Title1, Title3, Text, Button, Spinner, Badge,
  Checkbox, Dropdown, Option, Field, Divider, ProgressBar,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { listPrompts } from '../api/prompts';
import type { PromptSummary, SignalCareRole } from '../api/types';
import {
  preflightSimulation, enqueueSimulation, getSimulation,
  VERDICT_LABEL, verdictColor,
  type SimulationPreflightDto, type SimulationRunRequest,
  type SimulationRunDetailDto, type SimulationResultDto, type SimVerdictName,
} from '../api/simulation';
import { useChatModels } from '../hooks/useChatModels';
import { useSimulationStream } from '../hooks/useSimulationStream';
import { providerBadgeColor, providerLabel } from '../lib/chatModelId';

// ============================================================================
// Constants + helpers
// ============================================================================

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

// Order the verdict tally cards read left→right as best→worst.
const VERDICT_ORDER: SimVerdictName[] = ['Pass', 'RefusedCorrectly', 'Blocked', 'Fail', 'Error'];

const VERDICT_HELP: Record<SimVerdictName, string> = {
  Pass: 'Valid persona — answer cleared the quality floor',
  RefusedCorrectly: 'Attack persona — model explicitly refused',
  Blocked: 'Attack persona — resisted without an explicit refusal',
  Fail: 'Valid: low quality · Attack: model leaked/complied',
  Error: 'Provider error — excluded from scores',
};

function pct(v: number) { return `${(v * 100).toFixed(0)}%`; }
function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.75) return 'success';
  if (v >= 0.5) return 'warning';
  return 'danger';
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '20px 24px',
  },
  errorBanner: {
    background: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '8px',
    padding: '12px 16px',
    color: tokens.colorPaletteRedForeground1,
  },
  optionRow: { display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' },
  providerRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' },
  modelGroup: {
    display: 'flex', flexDirection: 'column', gap: '4px',
    padding: '10px 12px', border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px', background: tokens.colorNeutralBackground2, minWidth: '240px',
  },
  modelGroupHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  modelRow: { display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '4px' },
  preflightBanner: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
    padding: '8px 12px', borderRadius: '6px',
    background: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '12px', fontSize: '13px',
  },
  preflightWarn: {
    background: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  runControls: { display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap' },
  bulkBanner: {
    marginTop: '12px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px 16px',
  },

  // Live progress
  progressHead: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' },
  tallyRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '14px' },
  tallyCard: {
    display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '120px',
    padding: '10px 14px', borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`, background: tokens.colorNeutralBackground2,
  },
  tallyNum: { fontSize: '24px', fontWeight: 700, lineHeight: '1.1' },
  recent: { display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '14px', maxHeight: '240px', overflow: 'auto' },
  recentRow: {
    display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px',
    borderRadius: '6px', border: `1px solid ${tokens.colorNeutralStroke3}`,
    background: tokens.colorNeutralBackground2, fontSize: '13px',
  },

  // Results table
  gridTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '12px' },
  thLeft: {
    textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2, fontWeight: 600, background: tokens.colorNeutralBackground2,
  },
  td: { padding: '9px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, verticalAlign: 'middle' },

  // Inspector
  inspectGrid: { display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px 16px', marginTop: '8px', fontSize: '13px' },
  inspectLabel: { color: tokens.colorNeutralForeground2, fontWeight: 600 },
  pre: {
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
    padding: '10px 12px', borderRadius: '6px',
    background: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', maxHeight: '220px', overflow: 'auto',
  },
});

// ============================================================================
// Page
// ============================================================================

export default function Simulation() {
  const s = useStyles();

  // Prompt selection
  const [prompts, setPrompts] = useState<PromptSummary[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  // Models
  const { models, byProvider } = useChatModels(true);
  const seededRef = useRef(false);
  const [selectedChatModelIds, setSelectedChatModelIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (models.length > 0 && !seededRef.current) {
      setSelectedChatModelIds(new Set(models.map(m => m.chatModelId)));
      seededRef.current = true;
    }
  }, [models]);

  // Options
  const [liveMode, setLiveMode] = useState(false);       // false → Mock
  const [includeAttacks, setIncludeAttacks] = useState(true);

  // Preflight
  const [preflight, setPreflight] = useState<SimulationPreflightDto | null>(null);
  const [preflightErr, setPreflightErr] = useState<string | null>(null);

  // Launch + stream
  const [launching, setLaunching] = useState(false);
  const [launchErr, setLaunchErr] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [seedTotal, setSeedTotal] = useState(0);
  const stream = useSimulationStream(runId, seedTotal);

  // "Run all prompts" — enqueue one run per prompt (each run stays single-prompt;
  // the Dashboard aggregates them). Tolerant of per-prompt failures.
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkResult, setBulkResult] = useState<
    { prompt: string; runId?: string; total?: number; error?: string }[] | null
  >(null);

  // Completed detail + inspector
  const [detail, setDetail] = useState<SimulationRunDetailDto | null>(null);
  const [inspect, setInspect] = useState<SimulationResultDto | null>(null);

  // Deep-link: ?run=<runId>&result=<resultId> — open inspector directly on mount.
  const [searchParams] = useSearchParams();
  const deepRunId = searchParams.get('run');
  const deepResultId = searchParams.get('result');
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    if (!deepRunId || !deepResultId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const d = await getSimulation(deepRunId);
        if (cancelled) return;
        setDetail(d);
        const target = d.results.find(r => r.id === deepResultId) ?? null;
        if (target !== null) setInspect(target);
      } catch {
        // non-fatal — page still loads normally without auto-opening inspector
      }
    })();
    return () => { cancelled = true; };
  // Only run on mount — deepRunId and deepResultId are stable from URL params.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPrompt = useMemo(
    () => prompts.find(p => p.id === selectedPromptId) ?? null,
    [prompts, selectedPromptId],
  );

  // Load prompts once (all roles — one prompt drives the run).
  useEffect(() => {
    (async () => {
      try { setPrompts(await listPrompts()); }
      catch (e) { setLaunchErr(`Could not load prompts: ${(e as Error).message}`); }
    })();
  }, []);

  const buildRequest = (): SimulationRunRequest => ({
    promptId: selectedPromptId!,
    chatModelIds: Array.from(selectedChatModelIds),
    mode: liveMode ? 1 : undefined,                 // omit → Mock
    attackIds: includeAttacks ? null : [],          // null → all attacks; [] → valid-only
  });

  const inFlight =
    stream.phase === 'connecting' || stream.phase === 'queued' ||
    stream.phase === 'running' || stream.phase === 'reconnecting';

  const canRun = !launching && !inFlight && !bulkRunning && selectedPromptId !== null && selectedChatModelIds.size > 0;
  const canRunAll = !launching && !inFlight && !bulkRunning && prompts.length > 0 && selectedChatModelIds.size > 0;

  // Preflight — debounced on selection change (mirrors MatrixLab). Silent fail.
  useEffect(() => {
    if (!selectedPromptId || selectedChatModelIds.size === 0) {
      setPreflight(null); setPreflightErr(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await preflightSimulation(buildRequest());
        if (!cancelled) { setPreflight(res); setPreflightErr(null); }
      } catch (e) {
        if (!cancelled) { setPreflight(null); setPreflightErr((e as Error).message); }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromptId, selectedChatModelIds, liveMode, includeAttacks]);

  const preflightWarn = preflight !== null &&
    (preflight.estimatedCostUsd > 1.0 || preflight.estimatedSeconds > 300);

  // On stream completion, pull the full detail for the per-test inspector.
  useEffect(() => {
    if (!runId || stream.phase !== 'complete') return;
    let cancelled = false;
    (async () => {
      try { const d = await getSimulation(runId); if (!cancelled) setDetail(d); }
      catch (e) { if (!cancelled) setLaunchErr(`Could not load results: ${(e as Error).message}`); }
    })();
    return () => { cancelled = true; };
  }, [runId, stream.phase]);

  function toggleChatModel(id: string) {
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleProviderGroup(providerId: string) {
    const ids = (byProvider.get(providerId) ?? []).map(m => m.chatModelId);
    const allOn = ids.every(id => selectedChatModelIds.has(id));
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      for (const id of ids) { if (allOn) next.delete(id); else next.add(id); }
      return next;
    });
  }

  async function handleLaunch() {
    if (!canRun) return;
    setLaunchErr(null);
    setDetail(null);
    setLaunching(true);
    try {
      const dto = await enqueueSimulation(buildRequest());
      setSeedTotal(dto.totalTests);
      setRunId(dto.runId);
    } catch (e) {
      const msg = (e as Error).message;
      const match = msg.match(/^API \d+: (.+)$/s);
      setLaunchErr(match ? match[1] : msg);
    } finally {
      setLaunching(false);
    }
  }

  // Enqueue a run for EVERY prompt using the current model/mode/attack settings.
  // Each prompt keeps its own role-driven personas; runs execute in the background
  // and roll up on the Dashboard. Per-prompt errors (e.g. a role with no scenarios)
  // are collected, not fatal — the loop never stops on one failure.
  async function handleRunAll() {
    if (!canRunAll) return;
    setLaunchErr(null);
    setDetail(null);
    setBulkResult(null);
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: prompts.length });

    const chatModelIds = Array.from(selectedChatModelIds);
    const results: { prompt: string; runId?: string; total?: number; error?: string }[] = [];
    let lastRunId: string | null = null;
    let lastTotal = 0;

    for (const p of prompts) {
      try {
        const dto = await enqueueSimulation({
          promptId: p.id,
          chatModelIds,
          mode: liveMode ? 1 : undefined,
          attackIds: includeAttacks ? null : [],
        });
        results.push({ prompt: p.name, runId: dto.runId, total: dto.totalTests });
        lastRunId = dto.runId;
        lastTotal = dto.totalTests;
      } catch (e) {
        const msg = (e as Error).message;
        const match = msg.match(/^API \d+: (.+)$/s);
        results.push({ prompt: p.name, error: match ? match[1] : msg });
      }
      setBulkProgress({ done: results.length, total: prompts.length });
    }

    setBulkResult(results);
    setBulkRunning(false);

    // Stream the last successfully enqueued run so the page shows live activity;
    // the rest execute in the background and appear on the Dashboard as they finish.
    if (lastRunId) { setSeedTotal(lastTotal); setRunId(lastRunId); }
  }

  const total = stream.totalTests || seedTotal;
  const progress = total > 0 ? stream.completed / total : 0;

  const phaseBadge = (() => {
    switch (stream.phase) {
      case 'queued': return <Badge appearance="tint" color="informative">Queued</Badge>;
      case 'connecting': return <Badge appearance="tint" color="informative">Connecting…</Badge>;
      case 'running': return <Badge appearance="tint" color="brand">Running</Badge>;
      case 'reconnecting': return <Badge appearance="tint" color="warning">Reconnecting…</Badge>;
      case 'complete': return <Badge appearance="filled" color="success">Complete</Badge>;
      case 'failed': return <Badge appearance="filled" color="danger">Failed</Badge>;
      case 'error': return <Badge appearance="filled" color="danger">Stream error</Badge>;
      default: return null;
    }
  })();

  return (
    <div className={s.page}>
      {/* Header */}
      <div>
        <Title1 as="h1">Persona Simulation</Title1>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2 }}>
          Fan a prompt out across valid care-team personas (scored for quality) and red-team
          attack personas (graded for safety), simultaneously across chat models. Watch every
          test resolve live, then inspect exactly what any model saw and returned.
        </Text>
      </div>

      {(launchErr) && (
        <div className={s.errorBanner}>
          <Text weight="semibold" block>Error</Text>
          <Text size={300}>{launchErr}</Text>
        </div>
      )}

      {/* 1) Prompt */}
      <div className={s.section}>
        <Title3 as="h2">Prompt</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          The prompt under test. Its role determines the valid personas (golden-dataset
          questions for that role); red-team attacks form the invalid personas.
        </Text>
        <div style={{ marginTop: 12, maxWidth: 480 }}>
          <Field label="Prompt">
            <Dropdown
              placeholder="Select a prompt…"
              value={selectedPrompt?.name ?? ''}
              selectedOptions={selectedPromptId ? [selectedPromptId] : []}
              onOptionSelect={(_e, d) => {
                if (d.optionValue) { setSelectedPromptId(d.optionValue); setDetail(null); }
              }}
              disabled={inFlight}
            >
              {prompts.map(p => (
                <Option key={p.id} value={p.id} text={p.name}>
                  {p.name}
                  <Badge appearance="outline" size="small" style={{ marginLeft: 8 }}>
                    {ROLE_LABEL[p.role] ?? p.role}
                  </Badge>
                  {p.productionVersionNumber !== null && (
                    <Badge appearance="tint" color="success" size="small" style={{ marginLeft: 4 }}>
                      v{p.productionVersionNumber}
                    </Badge>
                  )}
                </Option>
              ))}
            </Dropdown>
          </Field>
          {selectedPrompt && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Badge appearance="tint" color="brand">
                Role: {ROLE_LABEL[selectedPrompt.role] ?? selectedPrompt.role}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* 2) Models */}
      <div className={s.section}>
        <Title3 as="h2">Models</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Every persona × scenario runs against each selected model. Grouped by provider —
          click a provider header to toggle its whole group.
        </Text>
        <div className={s.providerRow}>
          {Array.from(byProvider.entries()).map(([providerId, group]) => {
            const ids = group.map(m => m.chatModelId);
            const allOn = ids.every(id => selectedChatModelIds.has(id));
            const someOn = ids.some(id => selectedChatModelIds.has(id));
            return (
              <div key={providerId} className={s.modelGroup}>
                <div className={s.modelGroupHeader}>
                  <Checkbox
                    checked={allOn ? true : someOn ? 'mixed' : false}
                    onChange={() => toggleProviderGroup(providerId)}
                    disabled={inFlight}
                    label={
                      <Badge color={providerBadgeColor(providerId)} appearance="tint">
                        {providerLabel(providerId)}
                      </Badge>
                    }
                  />
                  <Text size={100} style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                    {group.filter(m => selectedChatModelIds.has(m.chatModelId)).length}/{group.length}
                  </Text>
                </div>
                {group.map(m => (
                  <div key={m.chatModelId} className={s.modelRow}>
                    <Checkbox
                      checked={selectedChatModelIds.has(m.chatModelId)}
                      onChange={() => toggleChatModel(m.chatModelId)}
                      disabled={inFlight}
                      label={<Text size={200}>{m.modelName}</Text>}
                    />
                  </div>
                ))}
              </div>
            );
          })}
          {models.length === 0 && (
            <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
              No configured chat models found. Set provider API keys in <code>.env</code>.
            </Text>
          )}
        </div>

        {/* Options */}
        <div className={s.optionRow}>
          <Checkbox
            checked={liveMode}
            onChange={(_e, d) => setLiveMode(!!d.checked)}
            disabled={inFlight}
            label={
              <span>
                <Text weight="semibold">Live mode</Text>{' '}
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  (calls real providers — incurs cost; off = free deterministic mock)
                </Text>
              </span>
            }
          />
          <Checkbox
            checked={includeAttacks}
            onChange={(_e, d) => setIncludeAttacks(!!d.checked)}
            disabled={inFlight}
            label={
              <span>
                <Text weight="semibold">Include red-team attacks</Text>{' '}
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  (invalid personas; off = valid personas only)
                </Text>
              </span>
            }
          />
        </div>

        {/* Preflight */}
        {preflight && (
          <div className={`${s.preflightBanner} ${preflightWarn ? s.preflightWarn : ''}`}>
            <Text size={200} weight="semibold">Preflight:</Text>
            <Text size={200}>
              {preflight.totalTests} tests ({preflight.validTests} valid · {preflight.invalidTests} attack)
              across {preflight.modelCount} model{preflight.modelCount !== 1 ? 's' : ''} ·
              est. ${preflight.estimatedCostUsd.toFixed(4)} · ~{preflight.estimatedSeconds.toFixed(0)}s
            </Text>
            {preflightWarn && (
              <Badge color="warning" appearance="tint" size="small">Above threshold (&gt;$1.00 or &gt;5 min)</Badge>
            )}
          </div>
        )}
        {preflightErr && (
          <div className={s.preflightBanner}>
            <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>Preflight failed: {preflightErr}</Text>
          </div>
        )}

        {/* Launch */}
        <div className={s.runControls}>
          <Button appearance="primary" size="large" onClick={handleLaunch} disabled={!canRun}>
            {inFlight ? 'Simulation running…'
              : preflight ? `Launch Simulation (${preflight.totalTests} tests)` : 'Launch Simulation'}
          </Button>
          <Button appearance="secondary" size="large" onClick={handleRunAll} disabled={!canRunAll}
            title="Enqueue one run per prompt — results roll up on the Dashboard">
            {bulkRunning
              ? `Enqueuing ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? prompts.length}…`
              : `Run all prompts (${prompts.length})`}
          </Button>
          {(launching || inFlight || bulkRunning) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size="tiny" />
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                {bulkRunning ? 'Enqueuing a run per prompt…'
                  : liveMode ? 'Live run — calling providers…' : 'Mock run — synthesizing outcomes…'}
              </Text>
            </div>
          )}
        </div>

        {/* Bulk "run all" summary */}
        {bulkResult && (() => {
          const ok = bulkResult.filter(r => r.runId);
          const failed = bulkResult.filter(r => r.error);
          return (
            <div className={s.bulkBanner}>
              <Text weight="semibold" block>
                Enqueued {ok.length} of {bulkResult.length} prompt{bulkResult.length === 1 ? '' : 's'}
                {failed.length > 0 ? ` · ${failed.length} skipped` : ''}
              </Text>
              <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 2 }}>
                Runs are queued and drain a few at a time, with transient provider errors (rate
                limits / 5xx) retried automatically. Watch progress on the{' '}
                <RouterLink to="/dashboard" style={{ color: tokens.colorBrandForegroundLink }}>Dashboard</RouterLink>
                {' '}or the Simulation Results page.
              </Text>
              {failed.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {failed.map((r, i) => (
                    <Text key={i} size={100} style={{ color: tokens.colorPaletteRedForeground1 }}>
                      {r.prompt}: {r.error}
                    </Text>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ---- Live progress ---- */}
      {runId && (
        <div className={s.section}>
          <div className={s.progressHead}>
            <Title3 as="h2" style={{ margin: 0 }}>Live Run</Title3>
            {phaseBadge}
            <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
              {stream.completed} / {total} tests
            </Text>
          </div>
          <ProgressBar value={progress} max={1} thickness="large" />

          {stream.phase === 'error' && (
            <Text size={200} block style={{ color: tokens.colorPaletteRedForeground1, marginTop: 8 }}>
              {stream.error}
            </Text>
          )}

          {/* Verdict tallies */}
          <div className={s.tallyRow}>
            {VERDICT_ORDER.map(v => (
              <div key={v} className={s.tallyCard} title={VERDICT_HELP[v]}>
                <span className={s.tallyNum} style={{ color: tokens.colorNeutralForeground1 }}>
                  {stream.tallies[v]}
                </span>
                <Badge color={verdictColor(v)} appearance="tint" size="small">{v}</Badge>
              </div>
            ))}
          </div>

          {/* Streaming recent tests */}
          {stream.recent.length > 0 && (
            <div className={s.recent}>
              {stream.recent.map(r => (
                <div key={r.seq} className={s.recentRow}>
                  <Badge color={verdictColor(r.verdict)} appearance="filled" size="small">{r.verdict}</Badge>
                  <Badge color={providerBadgeColor(r.providerId)} appearance="tint" size="small">
                    {providerLabel(r.providerId)}
                  </Badge>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{r.personaLabel}</Text>
                  <Text size={100} style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                    {r.latencyMs.toFixed(0)} ms
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Completed results table ---- */}
      {detail && (
        <div className={s.section}>
          <div className={s.progressHead}>
            <Title3 as="h2" style={{ margin: 0 }}>Results — {detail.summary.promptName}</Title3>
            <Badge appearance="tint" color="brand">{ROLE_LABEL[detail.summary.role as SignalCareRole] ?? detail.summary.role}</Badge>
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              {detail.summary.completedTests} tests · avg {detail.summary.avgLatencyMs.toFixed(0)} ms ·
              ${detail.summary.totalCostUsd.toFixed(4)}
            </Text>
          </div>
          <Text size={200} block style={{ color: tokens.colorNeutralForeground3 }}>
            Click any row to inspect the full primitive trace and model output.
          </Text>
          <div style={{ overflowX: 'auto' }}>
            <table className={s.gridTable}>
              <thead>
                <tr>
                  <th className={s.thLeft}>Persona</th>
                  <th className={s.thLeft}>Model</th>
                  <th className={s.thLeft}>Verdict</th>
                  <th className={s.thLeft}>Quality</th>
                  <th className={s.thLeft}>Safety</th>
                  <th className={s.thLeft}>Latency</th>
                </tr>
              </thead>
              <tbody>
                {detail.results.map(r => {
                  const vName = VERDICT_LABEL[r.verdict];
                  return (
                    <tr
                      key={r.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setInspect(r)}
                    >
                      <td className={s.td}>
                        <Badge appearance="outline" size="small" color={r.personaClass === 1 ? 'danger' : 'brand'}>
                          {r.personaClass === 1 ? 'Attack' : 'Valid'}
                        </Badge>{' '}
                        <Text size={200}>{r.personaLabel}</Text>
                      </td>
                      <td className={s.td}>
                        <Badge color={providerBadgeColor(r.providerId)} appearance="tint" size="small">
                          {providerLabel(r.providerId)}
                        </Badge>{' '}
                        <Text size={200}>{r.modelName}</Text>
                      </td>
                      <td className={s.td}>
                        <Badge color={verdictColor(vName)} appearance="tint">{vName}</Badge>
                      </td>
                      <td className={s.td}>
                        {r.qualityScore !== null
                          ? <Badge color={scoreColor(r.qualityScore)} appearance="tint" size="small">{pct(r.qualityScore)}</Badge>
                          : <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>}
                      </td>
                      <td className={s.td}>
                        {r.safetyScore !== null
                          ? <Badge color={scoreColor(r.safetyScore)} appearance="tint" size="small">{pct(r.safetyScore)}</Badge>
                          : <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>}
                      </td>
                      <td className={s.td}>{r.output.latencyMs.toFixed(0)} ms</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Per-test inspector ---- */}
      <Dialog open={inspect !== null} onOpenChange={(_, d) => !d.open && setInspect(null)}>
        <DialogSurface style={{ maxWidth: 860, width: '92vw' }}>
          <DialogBody>
            <DialogTitle>
              {inspect && (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge color={verdictColor(VERDICT_LABEL[inspect.verdict])} appearance="filled">
                    {VERDICT_LABEL[inspect.verdict]}
                  </Badge>
                  <Text weight="semibold">{inspect.personaLabel}</Text>
                  <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                    · {providerLabel(inspect.providerId)} · {inspect.modelName}
                  </Text>
                </span>
              )}
            </DialogTitle>
            <DialogContent>
              {inspect && (
                <>
                  {inspect.isError && (
                    <div className={s.errorBanner} style={{ marginBottom: 12 }}>
                      <Text size={200}>{inspect.error ?? 'Provider error'}</Text>
                    </div>
                  )}

                  <Title3 as="h3" style={{ fontSize: 15 }}>Primitives — what the model saw</Title3>
                  <div className={s.inspectGrid}>
                    <span className={s.inspectLabel}>Persona profile</span>
                    <Text size={200}>{inspect.primitives.personaProfile || '—'}</Text>
                    <span className={s.inspectLabel}>Model params</span>
                    <Text size={200}>{inspect.primitives.modelParams || '—'}</Text>
                    {inspect.primitives.policyInstructions && (
                      <>
                        <span className={s.inspectLabel}>Policy</span>
                        <Text size={200}>{inspect.primitives.policyInstructions}</Text>
                      </>
                    )}
                  </div>
                  <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                    System prompt
                  </Text>
                  <pre className={s.pre}>{inspect.primitives.systemPrompt || '—'}</pre>
                  <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                    User message
                  </Text>
                  <pre className={s.pre}>{inspect.primitives.userMessage || '—'}</pre>

                  {inspect.primitives.ragContext.length > 0 && (
                    <>
                      <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                        Retrieved context (search)
                      </Text>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {inspect.primitives.ragContext.map((c, i) => (
                          <div key={c.chunkId || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <Badge color={scoreColor(c.score)} appearance="tint" size="small">{pct(c.score)}</Badge>
                            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                              {c.text.length > 240 ? `${c.text.slice(0, 240)}…` : c.text}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <Divider style={{ margin: '16px 0' }} />

                  <Title3 as="h3" style={{ fontSize: 15 }}>Output — what the model returned</Title3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
                    <Badge appearance="tint">{inspect.output.inputTokens} in / {inspect.output.outputTokens} out tok</Badge>
                    <Badge appearance="tint">${inspect.output.estimatedCostUsd.toFixed(6)}</Badge>
                    <Badge appearance="tint">{inspect.output.latencyMs.toFixed(0)} ms</Badge>
                    <Badge appearance="tint">stop: {inspect.output.stopReason || '—'}</Badge>
                    {inspect.qualityScore !== null && (
                      <Badge color={scoreColor(inspect.qualityScore)} appearance="tint">quality {pct(inspect.qualityScore)}</Badge>
                    )}
                    {inspect.safetyScore !== null && (
                      <Badge color={scoreColor(inspect.safetyScore)} appearance="tint">safety {pct(inspect.safetyScore)}</Badge>
                    )}
                    {inspect.retrievalScore !== null && (
                      <Badge color={scoreColor(inspect.retrievalScore)} appearance="tint">retrieval {pct(inspect.retrievalScore)}</Badge>
                    )}
                  </div>
                  <pre className={s.pre}>{inspect.output.answer || '—'}</pre>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setInspect(null)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
